import React, { useState, useEffect } from 'react';
import dataService from '../services/dataService';
import { useToast } from '../context/ToastContext';
import { formatCurrency } from '../utils/formatCurrency';
import { Download } from 'lucide-react';
import './Reports.css';

function VATScreen() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quarter, setQuarter] = useState("Q1");
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const { addToast } = useToast();

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const res = await dataService.getOrders("Invoice");
        setInvoices(res.data.data || []);
      } catch (err) {
        addToast("Failed to fetch invoices for VAT report", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [addToast]);

  const filteredInvoices = invoices.filter(inv => {
    const date = new Date(inv.date);
    const invYear = date.getFullYear().toString();
    const invMonth = date.getMonth() + 1;
    
    let invQuarter = "";
    if (invMonth >= 1 && invMonth <= 3) invQuarter = "Q1";
    else if (invMonth >= 4 && invMonth <= 6) invQuarter = "Q2";
    else if (invMonth >= 7 && invMonth <= 9) invQuarter = "Q3";
    else invQuarter = "Q4";

    return invYear === year && invQuarter === quarter;
  });

  const taxableSales = filteredInvoices.reduce((acc, inv) => acc + (inv.amount - (inv.tax || 0)), 0);
  const vatCollected = filteredInvoices.reduce((acc, inv) => acc + (inv.tax || 0), 0);
  
  // For VAT reclaimable, we'd ideally pull from Purchases/Expenses that are Taxable
  // For this version, we calculate based on recorded data
  const vatPayable = vatCollected; // Simplified logic

  const handleExportVAT = () => {
    addToast("Exporting VAT Return PDF...", "success");
    // Placeholder for actual PDF generation specific to VAT
  };

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>VAT Submission Portal</h1>
          <p>Calculate and prepare your quarterly VAT returns for UAE FTA</p>
        </div>
        <button className="primary-btn" onClick={handleExportVAT}>
          <Download size={18} /> Export VAT Return
        </button>
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Taxable Sales (Output)</span>
            <h2 className="stat-value">{formatCurrency(taxableSales)}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">VAT Collected (Output)</span>
            <h2 className="stat-value" style={{ color: '#00A859' }}>{formatCurrency(vatCollected)}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">VAT Reclaimable (Input)</span>
            <h2 className="stat-value" style={{ color: '#2563eb' }}>{formatCurrency(0)}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Net VAT Payable</span>
            <h2 className="stat-value" style={{ color: '#EA580C' }}>{formatCurrency(vatPayable)}</h2>
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls">
          <div style={{ display: 'flex', gap: '15px' }}>
             <div className="input-group" style={{ marginBottom: 0 }}>
               <label style={{ fontSize: '12px' }}>Tax Year</label>
               <select value={year} onChange={(e) => setYear(e.target.value)}>
                 <option value="2024">2024</option>
                 <option value="2025">2025</option>
                 <option value="2026">2026</option>
               </select>
             </div>
             <div className="input-group" style={{ marginBottom: 0 }}>
               <label style={{ fontSize: '12px' }}>Tax Quarter</label>
               <select value={quarter} onChange={(e) => setQuarter(e.target.value)}>
                 <option value="Q1">Q1 (Jan - Mar)</option>
                 <option value="Q2">Q2 (Apr - Jun)</option>
                 <option value="Q3">Q3 (Jul - Sep)</option>
                 <option value="Q4">Q4 (Oct - Dec)</option>
               </select>
             </div>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Customer</th>
                <th className="text-right">Taxable Amount</th>
                <th className="text-right">VAT (5%)</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ textAlign: 'center' }}>Loading...</td></tr>
              ) : filteredInvoices.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign: 'center' }}>No transactions found for this period.</td></tr>
              ) : (
                filteredInvoices.map(inv => (
                  <tr key={inv._id}>
                    <td>{inv.orderId}</td>
                    <td>{new Date(inv.date).toLocaleDateString()}</td>
                    <td>{inv.customer}</td>
                    <td className="text-right">{formatCurrency(inv.amount - (inv.tax || 0))}</td>
                    <td className="text-right">{formatCurrency(inv.tax || 0)}</td>
                    <td className="text-right">{formatCurrency(inv.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default VATScreen;
